/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { merge, take } from 'rxjs';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  MemberDetailsFormService,
  TellentAccessService,
  TellentAppsDataFacade,
  TellentMembersDataFacade,
} from '@core/tellent-logic';
import {
  AddMemberForm,
  BRAND,
  InviteMemberPayload,
  InviteMemberPayloadNew,
  InviteMethod,
  LoginType,
  MEMBER_LISTED_ONLY,
  SeatKind,
  SeatType,
  TellentBrand,
  TellentBrandsDefaultRoles,
  TellentClientDialogResult,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
  UserInvitationFlow,
  UserInvitationFlowData,
} from '@core/tellent-types';
import { TellentComponentsNotificationService } from '@core/tellent-util';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  FormFieldComponent,
  IconComponent,
  LabelComponent,
  SelectComponent,
} from '@core/user-interface-legacy';
import { macroTask } from '@core/utils-legacy';

import { AddMemberAnalyticsService } from './add-member-analytics';
import { AccessRolesTableComponent } from '../access-roles-table/access-roles-table.component';
import { AccessRolesTableService } from '../access-roles-table/access-roles-table.service';
import { EditMemberPermissionsBoxComponent } from '../edit-member-admin-dialog/edit-member-permissions-access-tab/edit-member-permissions-box/edit-member-permissions-box.component';
import { GiveAdminPermissionsDialogComponent } from '../give-admin-permissions-dialog/give-admin-permissions-dialog.component';
import { AddMemberDetailsFormComponent } from './add-member-details-form/add-member-details-form.component';

type ProductSeat =
  | Partial<{
      toggle: boolean | null;
      appGuid: string | null;
      roleExternalId: string | null;
    }>
  | undefined;

enum DialogVariant {
  Simplified = 'simplified',
  Complex = 'complex',
}

type AddMemberDialogData = any;
type AddMemberDialogResult = TellentClientDialogResult<any>;

@Component({
  selector: 'tlc-add-member-dialog-new',
  templateUrl: './add-member-dialog-new.component.html',
  styleUrls: ['./add-member-dialog-new.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    FormFieldComponent,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    LabelComponent,
    TranslatePipe,
    SelectComponent,
    NgTemplateOutlet,
    ButtonLoadingDirective,
    AccessRolesTableComponent,
    AddMemberDetailsFormComponent,
    EditMemberPermissionsBoxComponent,
    InfoboxComponent,
  ],
  providers: [MemberDetailsFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMemberDialogNewComponent
  implements DialogComponentI<AddMemberDialogData, AddMemberDialogResult>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appsDataFacade = inject(TellentAppsDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly accessRolesTableSrv = inject(AccessRolesTableService);
  private readonly analytics = inject(AddMemberAnalyticsService);
  private readonly memberInvitationFormService = inject(MemberDetailsFormService);
  private readonly router = inject(Router);

  public readonly brand = inject(BRAND);
  public readonly tellentAccessService = inject(TellentAccessService);
  public readonly DialogVariant = DialogVariant;
  public readonly TellentBrand = TellentBrand;
  public readonly InviteMethod = InviteMethod;

  public webComponentOutput: AddMemberDialogResult;

  public i18n = inject(I18nService);
  public notificationService = inject(TellentComponentsNotificationService);

  public formGroup = this.formBuilder.group<AddMemberForm>({
    inviteMethod: new FormControl(InviteMethod.Email),
    loginType: new FormControl(LoginType.Auto),
    members: this.formBuilder.array([
      this.memberInvitationFormService.createMemberFormGroup(InviteMethod.Email),
    ]),
    seats: this.formBuilder.group({
      recruitee: this.createSeatFormGroup(),
      kiwi: this.createSeatFormGroup(),
    }),
  });

  public get members() {
    return this.formGroup.controls.members.controls;
  }

  public get seats() {
    return this.formGroup.controls.seats.controls;
  }

  public get inviteMethodControl(): FormControl<InviteMethod | null> {
    return this.formGroup.controls.inviteMethod;
  }

  public recruiteeSelectOptions = this.appsDataFacade.rolesSelectOptions(SeatKind.Recruitment);
  public kiwiSelectOptions = this.appsDataFacade.rolesSelectOptions(SeatKind.CoreHR);

  public recruiteeApp$ = this.appsDataFacade.appBySeatKind(SeatKind.Recruitment);

  public kiwiApp$ = this.appsDataFacade.appBySeatKind(SeatKind.CoreHR);

  public defaultRecruiteeRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.Recruitment);
  public defaultKiwiRole = this.appsDataFacade.defaultRoleBySeatKind(SeatKind.CoreHR);

  public loading$ = signal(false);
  public dialogVariant = signal(DialogVariant.Simplified);
  public userInvitationFlow = signal(UserInvitationFlow.AddMemberBasic);
  public displayAdvancedInvitationForm = signal<boolean>(false);
  public hasAtLeastOneSeatSelected = signal<boolean>(false);
  public selectedAdminAbilities = signal<Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>>(
    [],
  );

  public canManageTellentUsers = this.tellentAccessService.canManageTellentUsers;
  public canManageTellent = this.tellentAccessService.canManageTellent;

  public setDefaultRoles = effect(() => {
    const defaultRecruiteeRole = this.defaultRecruiteeRole();
    const defaultKiwiRole = this.defaultKiwiRole();

    if (defaultRecruiteeRole || defaultKiwiRole) {
      const defaults: TellentBrandsDefaultRoles = {
        recruitee: defaultRecruiteeRole,
        kiwi: defaultKiwiRole,
      };

      this.accessRolesTableSrv.applyDefaults(defaults, this.seats.recruitee, this.seats.kiwi);

      macroTask(() => this.formGroup.updateValueAndValidity());
    }
  });

  public ngAfterViewInit(): void {
    const seats = this.formGroup.controls.seats.controls;
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.Recruitment, seats.recruitee);
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.CoreHR, seats.kiwi);

    this.listenToInviteMethodChanges();

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(formValue => {
      const seats = formValue.seats;
      this.hasAtLeastOneSeatSelected.set(
        seats?.recruitee?.toggle === true || seats?.kiwi?.toggle === true,
      );
    });
  }

  public setDialogData(data: any, triggerUpdate: boolean): void {
    this.appsDataFacade.fetchCollection();
    this.dialogVariant.set(this.resolveDialogVariant());
    this.userInvitationFlow.set(data.flowType || UserInvitationFlow.AddMemberBasic);
    this.analytics.trackDialogOpened(data?.context || null);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    this.loading$.set(true);
    const { inviteMethod } = this.formGroup.value;
    this.formGroup.markAsPristine();

    switch (inviteMethod) {
      case InviteMethod.Email:
        // STATE 1: Invite via email
        this.inviteMembersViaEmail();
        break;
      case InviteMethod.Login:
        // STATE 2: Generate login
        this.addMemberWithLogin();
        break;
      default:
        break;
    }

    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  public toggleAdvancedInvitationForm(): void {
    this.displayAdvancedInvitationForm.update(display => !display);
    if (!this.displayAdvancedInvitationForm())
      this.inviteMethodControl.setValue(InviteMethod.Email);
  }

  public openGiveAdminPermissionsDialog(): void {
    this.dialogService
      .openDialog(
        GiveAdminPermissionsDialogComponent,
        { abilities: this.selectedAdminAbilities() },
        { width: DialogWidth.Small },
      )
      .afterClosed.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) this.selectedAdminAbilities.set(result.abilities);
      });
  }

  public navigateToCsvImport(): void {
    this.dialogRef.close();
    this.router.navigate(['team-members', 'import']);
  }

  private trackSubmitSuccess(
    addQuantity: number,
    recruiteeSeat: false | string,
    kiwiSeat: false | string,
  ): void {
    this.analytics.trackSent(addQuantity, recruiteeSeat, kiwiSeat);
  }

  private createSeatFormGroup(): FormGroup<SeatType> {
    return this.formBuilder.group<SeatType>({
      toggle: new FormControl<boolean | null>(null),
      appGuid: new FormControl<string | null>(''),
      roleExternalId: new FormControl<string | null>(''),
    });
  }

  private resolveDialogVariant(): DialogVariant {
    return this.canManageTellentUsers() ? DialogVariant.Complex : DialogVariant.Simplified;
  }

  private handleSubmitSuccess(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberSuccess$,
      this.membersDataFacade.inviteAppMemberSuccess$,
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ payload }) => {
        this.formGroup.markAsPristine();
        this.loading$.set(false);
        const { inviteMethod, loginType } = this.formGroup.value;
        const flowData: UserInvitationFlowData = {
          payload,
          inviteMethod,
          loginType,
        };

        switch (inviteMethod) {
          case InviteMethod.Email:
            this.dialogRef.close({
              ...flowData,
            });
            break;
          case InviteMethod.Login:
            this.dialogRef.close({
              ...flowData,
            });
            break;
          default:
            break;
        }
      });
  }

  private handleSubmitFailure(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberFail$,
      this.membersDataFacade.inviteAppMemberFail$,
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loading$.set(false));
  }

  // Autj - short for "Assign users/members to jobs"
  private shouldDisplayAutj(): boolean {
    const selectedRoleExternalId = this.seats.recruitee.value.roleExternalId;
    if (selectedRoleExternalId && this.recruiteeApp$()) {
      const selectedRoleAbilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended> =
        this.recruiteeApp$()!.roles.find(
          role => role.externalId === selectedRoleExternalId,
        )!.abilities!;
      return !selectedRoleAbilities.some(
        ability => ability === TellentOrgAbilitiesExtended.RecruitmentViewAllJobs,
      );
    }
    return false;
  }

  private prepareActionPayload(): { payload: InviteMemberPayload } {
    const { members, seats, inviteMethod, loginType } = this.formGroup.value;

    const kiwiSeat = seats?.kiwi;
    const recruiteeSeat = seats?.recruitee;

    const hasMemberListedOnlyAbility = kiwiSeat?.roleExternalId === MEMBER_LISTED_ONLY;

    const payloadSeats = [
      {
        includeInPayload: kiwiSeat?.toggle || hasMemberListedOnlyAbility,
        access: kiwiSeat?.toggle && !hasMemberListedOnlyAbility,
        appGuid: this.kiwiApp$()?.guid,
        roleExternalId: !hasMemberListedOnlyAbility ? kiwiSeat?.roleExternalId : null,
      },
      {
        includeInPayload: recruiteeSeat?.toggle,
        access: recruiteeSeat?.toggle,
        appGuid: this.recruiteeApp$()?.guid,
        roleExternalId: recruiteeSeat?.roleExternalId,
      },
    ]
      .filter(seat => seat.appGuid && seat.includeInPayload)
      .map(seat => ({
        access: seat.access,
        appGuid: seat.appGuid,
        roleExternalId: seat.roleExternalId,
      }));

    return {
      payload: <InviteMemberPayloadNew>{
        members,
        seats: payloadSeats,
        sendInvitation: inviteMethod === InviteMethod.Email,
        generateLogin: inviteMethod === InviteMethod.Login && loginType === LoginType.Auto,
        abilities: this.selectedAdminAbilities(),
      },
    };
  }

  private getRecruiteeRoleIdIfExists(recruiteeSeat: ProductSeat): string | false {
    return recruiteeSeat?.toggle
      ? this.recruiteeSelectOptions().find(role => role.value === recruiteeSeat?.roleExternalId!)
          ?.value!
      : false;
  }

  private getKiwiRoleIdIfExists(kiwiSeat: ProductSeat): string | false {
    return kiwiSeat?.toggle
      ? this.kiwiSelectOptions().find(role => role.value === kiwiSeat?.roleExternalId!)?.value!
      : false;
  }

  private inviteMembersViaEmail(): void {
    if (this.shouldDisplayAutj()) {
      const payload = this.prepareActionPayload();
      const seats = this.formGroup.value.seats;
      const { inviteMethod } = this.formGroup.value;

      this.formGroup.markAsPristine();

      this.dialogRef.close({
        ...payload,
        inviteMethod,
        displayAutj: true,
        recruiteeRoleId: this.getRecruiteeRoleIdIfExists(seats?.recruitee),
        kiwiRoleId: this.getKiwiRoleIdIfExists(seats?.kiwi),
      });
    } else {
      this.submitMembersWithoutSecondStep();
    }
  }

  private submitMembersWithoutSecondStep(): void {
    this.loading$.set(true);

    const { members, seats } = this.formGroup.value;

    const kiwiSeat = seats?.kiwi;
    const recruiteeSeat = seats?.recruitee;

    const actionPayload = this.prepareActionPayload();

    if (this.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(actionPayload.payload);
    } else {
      this.membersDataFacade.inviteAppMember(actionPayload.payload);
    }

    this.trackSubmitSuccess(
      members?.length!,
      this.getRecruiteeRoleIdIfExists(recruiteeSeat),
      this.getKiwiRoleIdIfExists(kiwiSeat),
    );

    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  private addMemberWithLogin(): void {
    const payload = this.prepareActionPayload();

    if (this.canManageTellentUsers()) {
      this.membersDataFacade.inviteTellentMember(payload.payload);
    } else {
      this.membersDataFacade.inviteAppMember(payload.payload);
    }
  }

  private listenToInviteMethodChanges(): void {
    this.formGroup.controls.inviteMethod.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(inviteMethod => {
        switch (inviteMethod) {
          case InviteMethod.Email:
            this.formGroup.controls.seats.controls.recruitee.enable({ emitEvent: false });
            break;
          case InviteMethod.Login:
            this.adjustAccessAndRolesControls();
            break;
          default:
            break;
        }
      });
  }

  // When adding a member via username and password, we need to ensure that Recruitee seat and Kiwi Administrator role are disabled.
  // Only Kiwi seat should be enabled with Employee role.
  // Also reset selected admin permissions.
  private adjustAccessAndRolesControls(): void {
    const kiwiValue = this.formGroup.controls.seats.controls.kiwi.value;

    this.formGroup.controls.seats.controls.recruitee.disable({ emitEvent: false });
    this.formGroup.controls.seats.controls.recruitee.reset(
      { toggle: null, appGuid: '', roleExternalId: '' },
      { emitEvent: false },
    );
    this.formGroup.controls.seats.controls.kiwi.reset(
      { ...kiwiValue, roleExternalId: 'employee' },
      { emitEvent: false },
    );

    this.selectedAdminAbilities.set([]);
  }
}
